import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Wallet, ArrowDownCircle, ArrowUpCircle, Calendar, RefreshCw,
  Lock, CheckCircle2, AlertTriangle, Printer,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => (Number(n) || 0).toLocaleString();

export default function DailyCashBoxPage() {
  const { token } = useAuth();
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actualCashIqd, setActualCashIqd] = useState("");
  const [actualCashUsd, setActualCashUsd] = useState("");
  const [closureNotes, setClosureNotes] = useState("");
  const [closing, setClosing] = useState(false);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/cash-box/daily?date=${date}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSummary(res.data);
      if (res.data.is_closed && res.data.closure) {
        setActualCashIqd(String(res.data.closure.actual_cash_iqd || ""));
        setActualCashUsd(String(res.data.closure.actual_cash_usd || ""));
        setClosureNotes(res.data.closure.notes || "");
      } else {
        setActualCashIqd("");
        setClosureNotes("");
      }
    } catch (err) {
      toast.error("فشل تحميل البيانات");
    } finally {
      setLoading(false);
    }
  }, [date, token]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const expectedIqd = summary?.expected_closing_iqd || 0;
  const actualIqd = parseFloat(actualCashIqd) || 0;
  const differenceIqd = actualIqd - expectedIqd;

  const closeBox = async () => {
    if (!actualCashIqd) {
      toast.error("أدخل المبلغ الفعلي في الصندوق");
      return;
    }
    setClosing(true);
    try {
      await axios.post(`${API}/cash-box/close`, {
        closure_date: date,
        actual_cash_iqd: actualIqd,
        actual_cash_usd: parseFloat(actualCashUsd) || 0,
        notes: closureNotes,
        expected_iqd: expectedIqd,
        difference_iqd: differenceIqd,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("✅ تم إغلاق الصندوق");
      fetchSummary();
    } catch (err) {
      toast.error("فشل الإغلاق");
    } finally {
      setClosing(false);
    }
  };

  const InRow = ({ label, total, count, color = "emerald" }) => (
    <div className="flex justify-between items-center py-2 px-3 rounded-lg bg-slate-800/40 border border-slate-700">
      <div className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full bg-${color}-400`}></span>
        <span className="text-slate-200 text-sm">{label}</span>
        <span className="text-slate-500 text-xs">({count})</span>
      </div>
      <span className={`font-mono font-bold text-${color}-300`}>{fmt(total)} د.ع</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-950 p-4" dir="rtl">
      <div className="max-w-6xl mx-auto space-y-4">
        {/* Header */}
        <Card className="bg-gradient-to-l from-emerald-900/40 to-slate-900 border-emerald-500/30">
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <CardTitle className="text-white text-2xl flex items-center gap-2">
                <Wallet className="w-7 h-7 text-emerald-400" />
                كشف الصندوق اليومي
              </CardTitle>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400" />
                <Input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="bg-slate-800 border-slate-700 text-white w-48"
                  data-testid="cashbox-date-input"
                />
                <Button
                  onClick={fetchSummary}
                  variant="outline"
                  size="sm"
                  className="border-slate-600 text-slate-300"
                  data-testid="cashbox-refresh-btn"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        {summary && (
          <>
            {/* 4 Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="p-4 rounded-xl bg-slate-800/60 border border-slate-700">
                <p className="text-xs text-slate-400 mb-1">الرصيد الافتتاحي</p>
                <p className="text-2xl font-bold text-cyan-300 font-mono" data-testid="cashbox-opening">
                  {fmt(summary.opening_balance_iqd)}
                </p>
                <p className="text-[10px] text-slate-500 mt-1">د.ع</p>
              </div>
              <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
                <p className="text-xs text-emerald-300 mb-1 flex items-center gap-1">
                  <ArrowDownCircle className="w-3 h-3" />الداخل (Cash IN)
                </p>
                <p className="text-2xl font-bold text-emerald-300 font-mono" data-testid="cashbox-total-in">
                  +{fmt(summary.cash_in.total)}
                </p>
                <p className="text-[10px] text-slate-500 mt-1">د.ع</p>
              </div>
              <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/30">
                <p className="text-xs text-rose-300 mb-1 flex items-center gap-1">
                  <ArrowUpCircle className="w-3 h-3" />الخارج (Cash OUT)
                </p>
                <p className="text-2xl font-bold text-rose-300 font-mono" data-testid="cashbox-total-out">
                  −{fmt(summary.cash_out.total)}
                </p>
                <p className="text-[10px] text-slate-500 mt-1">د.ع</p>
              </div>
              <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
                <p className="text-xs text-amber-300 mb-1">الرصيد المتوقع</p>
                <p className="text-2xl font-bold text-amber-300 font-mono" data-testid="cashbox-expected">
                  {fmt(summary.expected_closing_iqd)}
                </p>
                <p className="text-[10px] text-slate-500 mt-1">د.ع</p>
              </div>
            </div>

            {/* Cash IN details */}
            <Card className="bg-slate-900/60 border-emerald-500/20">
              <CardHeader className="pb-3">
                <CardTitle className="text-emerald-300 text-lg flex items-center gap-2">
                  <ArrowDownCircle className="w-5 h-5" />
                  الحركات الداخلة
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <InRow label="مبيعات نقدية" total={summary.cash_in.cash_sales.total} count={summary.cash_in.cash_sales.count} />
                <InRow label="دفعات مقدمة على فواتير آجلة" total={summary.cash_in.credit_downpayments.total} count={summary.cash_in.credit_downpayments.count} />
                <InRow label="تسديدات الزبائن" total={summary.cash_in.customer_payments.total} count={summary.cash_in.customer_payments.count} />
                <InRow label="سندات قبض" total={summary.cash_in.receipt_vouchers.total} count={summary.cash_in.receipt_vouchers.count} />
                <div className="flex justify-between items-center py-3 px-3 rounded-lg bg-emerald-500/10 border border-emerald-500/40 mt-3">
                  <span className="text-emerald-200 font-bold">إجمالي الداخل</span>
                  <span className="font-mono font-bold text-emerald-300 text-xl">{fmt(summary.cash_in.total)} د.ع</span>
                </div>
              </CardContent>
            </Card>

            {/* Cash OUT details */}
            <Card className="bg-slate-900/60 border-rose-500/20">
              <CardHeader className="pb-3">
                <CardTitle className="text-rose-300 text-lg flex items-center gap-2">
                  <ArrowUpCircle className="w-5 h-5" />
                  الحركات الخارجة
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <InRow label="مشتريات نقدية" total={summary.cash_out.purchases.total} count={summary.cash_out.purchases.count} color="rose" />
                <InRow label="مصاريف" total={summary.cash_out.expenses.total} count={summary.cash_out.expenses.count} color="rose" />
                <InRow label="سندات صرف" total={summary.cash_out.payment_vouchers.total} count={summary.cash_out.payment_vouchers.count} color="rose" />
                <InRow label="مرتجعات (مبالغ مستردة)" total={summary.cash_out.returns.total} count={summary.cash_out.returns.count} color="rose" />
                <div className="flex justify-between items-center py-3 px-3 rounded-lg bg-rose-500/10 border border-rose-500/40 mt-3">
                  <span className="text-rose-200 font-bold">إجمالي الخارج</span>
                  <span className="font-mono font-bold text-rose-300 text-xl">{fmt(summary.cash_out.total)} د.ع</span>
                </div>
              </CardContent>
            </Card>

            {/* Reconciliation / Closure */}
            <Card className={`border-2 ${
              summary.is_closed ? "bg-blue-500/5 border-blue-500/30" : "bg-amber-500/5 border-amber-500/30"
            }`}>
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  {summary.is_closed
                    ? <><CheckCircle2 className="w-5 h-5 text-blue-400" /> تم إغلاق الصندوق ✓</>
                    : <><Lock className="w-5 h-5 text-amber-400" /> مطابقة و إغلاق الصندوق</>}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-slate-300 text-xs">المبلغ الفعلي بالدينار *</Label>
                    <Input
                      type="number"
                      inputMode="numeric"
                      value={actualCashIqd}
                      onChange={(e) => setActualCashIqd(e.target.value)}
                      placeholder="0"
                      className="bg-slate-800 border-slate-700 text-white text-lg font-mono"
                      data-testid="cashbox-actual-iqd-input"
                      disabled={summary.is_closed}
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-xs">المبلغ الفعلي بالدولار</Label>
                    <Input
                      type="number"
                      inputMode="decimal"
                      value={actualCashUsd}
                      onChange={(e) => setActualCashUsd(e.target.value)}
                      placeholder="0"
                      className="bg-slate-800 border-slate-700 text-white text-lg font-mono"
                      data-testid="cashbox-actual-usd-input"
                      disabled={summary.is_closed}
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-xs">الفرق</Label>
                    <div className={`px-3 py-2 rounded-md border font-mono text-lg font-bold ${
                      Math.abs(differenceIqd) < 1
                        ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300"
                        : differenceIqd > 0
                          ? "bg-amber-500/10 border-amber-500/40 text-amber-300"
                          : "bg-rose-500/10 border-rose-500/40 text-rose-300"
                    }`} data-testid="cashbox-difference">
                      {Math.abs(differenceIqd) < 1
                        ? "0 (مطابق ✓)"
                        : (differenceIqd > 0 ? `+${fmt(differenceIqd)} زائد` : `${fmt(differenceIqd)} ناقص`)}
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-slate-300 text-xs">ملاحظات (اختياري)</Label>
                  <textarea
                    value={closureNotes}
                    onChange={(e) => setClosureNotes(e.target.value)}
                    rows={2}
                    placeholder="مثال: تم إيداع 50,000 د.ع في البنك..."
                    className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-white text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                    data-testid="cashbox-closure-notes"
                    disabled={summary.is_closed}
                  />
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={closeBox}
                    disabled={closing || !actualCashIqd}
                    className={summary.is_closed
                      ? "bg-blue-600 hover:bg-blue-700 text-white"
                      : "bg-emerald-600 hover:bg-emerald-700 text-white"}
                    data-testid="cashbox-close-btn"
                  >
                    <Lock className="w-4 h-4 ms-2" />
                    {summary.is_closed ? "تحديث الإغلاق" : (closing ? "جاري..." : "إغلاق الصندوق")}
                  </Button>
                  <Button
                    onClick={() => window.print()}
                    variant="outline"
                    className="border-blue-600 text-blue-400 hover:bg-blue-500/10"
                    data-testid="cashbox-print-btn"
                  >
                    <Printer className="w-4 h-4 ms-2" />
                    طباعة الكشف
                  </Button>
                  {Math.abs(differenceIqd) > 1 && actualCashIqd && (
                    <div className="flex items-center gap-2 text-amber-400 text-xs">
                      <AlertTriangle className="w-4 h-4" />
                      توجد فجوة! تأكد من العد قبل الإغلاق
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {!summary && !loading && (
          <Card className="bg-slate-900 border-slate-700">
            <CardContent className="py-12 text-center">
              <Wallet className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">لا توجد بيانات</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
